export const WEEKLY_MEET_LINK = "https://meet.google.com/bam-coaches-weekly";

// The weekly call is anchored to BAM HQ time, not the viewer's local time —
// otherwise every user computes a different "next 7 PM" and they're not even
// the same call instant. Display in the user's tz happens later via
// fmtTimeInTz/fmtDayInTz; here we just produce the correct UTC instants.
//
// These constants are the fallback used until the recurring_call_config row
// is loaded from Supabase (or if that load fails). Admins edit the live row
// from /admin/group-calls, then BAMFull passes the row in as the second arg
// to getAllUpcomingCalls so the widget reflects the change immediately.
const FALLBACK_CONFIG = {
  day_of_week: 2,        // Tuesday (Sun=0); the schedule since 2026-10-20
  hour: 10,              // 10 AM local to tz
  minute: 0,
  tz: "America/Los_Angeles",
  duration_min: 30,
  title: "BAM Coaches Platform Weekly Call",
  meet_link: "https://meet.google.com/hsf-dksq-rdi",
};

/* How long a call runs. Configured per call (recurring_call_config
   .duration_min, 2026-10-15); an hour where it isn't set. */
export const callMinutes = (cfg) => {
  const n = Number(cfg?.duration_min);
  return Number.isFinite(n) && n > 0 ? n : 60;
};

// Wall-clock components of `date` interpreted in `tz`. Lets us answer
// "what's the date + day-of-week + hour in NY right now?" without a
// tz library.
function partsInTz(date, tz) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, weekday: "short",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date);
  const m = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return {
    year: parseInt(m.year, 10),
    month: parseInt(m.month, 10),
    day: parseInt(m.day, 10),
    hour: parseInt(m.hour, 10),
    minute: parseInt(m.minute, 10),
    dow: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].indexOf(m.weekday),
  };
}

// Convert a wall-clock datetime in `tz` to its UTC instant. Handles DST
// because Intl.DateTimeFormat respects the active offset for `tz` at that
// instant. Trick: interpret the wall time naively as UTC, see what tz
// renders that as, and subtract the gap.
function wallTimeToInstant(year, month, day, hour, minute, tz) {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const got = partsInTz(new Date(naive), tz);
  const gotUtc = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute);
  return naive + (naive - gotUtc);
}

export function getNextMondays(count, config) {
  const cfg = config || FALLBACK_CONFIG;
  const dates = [];
  const nowParts = partsInTz(new Date(), cfg.tz);
  // Days until next call from "today in the call's tz".
  let daysUntil;
  if (nowParts.dow === cfg.day_of_week) {
    // It's call-day in the configured tz — today's slot if before the
    // hour, else next week.
    const beforeSlot = nowParts.hour < cfg.hour
      || (nowParts.hour === cfg.hour && nowParts.minute < cfg.minute)
      || (nowParts.hour === cfg.hour && nowParts.minute === cfg.minute);
    daysUntil = beforeSlot ? 0 : 7;
  } else {
    daysUntil = (cfg.day_of_week - nowParts.dow + 7) % 7;
    if (daysUntil === 0) daysUntil = 7; // safety; shouldn't hit
  }
  for (let i = 0; i < count; i++) {
    // Compute each call's wall date independently so DST transitions
    // (twice a year) don't drift the instant by an hour.
    const target = new Date(Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day + daysUntil + i * 7));
    const inst = wallTimeToInstant(
      target.getUTCFullYear(),
      target.getUTCMonth() + 1,
      target.getUTCDate(),
      cfg.hour, cfg.minute, cfg.tz
    );
    dates.push(new Date(inst));
  }
  return dates;
}

/* The most recent start of a recurring call at or before `now`, as a UTC
   instant (the same wall-clock arithmetic as getNextMondays, so DST holds). */
export function lastStart(cfg, now = Date.now()) {
  const p = partsInTz(new Date(now), cfg.tz);
  const back = (p.dow - cfg.day_of_week + 7) % 7;
  const at = (daysBack) => {
    const d = new Date(Date.UTC(p.year, p.month - 1, p.day - daysBack));
    return wallTimeToInstant(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), cfg.hour, cfg.minute, cfg.tz);
  };
  const t = at(back);
  return t <= now ? t : at(back + 7);
}

/* The recurring call that is running right now, if any: started, and not
   yet past its length. Each call is checked against its own rhythm; this
   used to step the soonest upcoming call back a week, which missed a
   Tuesday call in progress whenever Thursday's was the next one. */
export function liveCall(configs, now = Date.now()) {
  for (const cfg of (configs || []).filter(c => c && c.day_of_week != null && c.tz)) {
    const start = lastStart(cfg, now);
    if (now < start + callMinutes(cfg) * 60000) {
      return { id: `weekly-${cfg.call_type || "platform"}-live`, title: cfg.title, dateTime: start,
        meetLink: cfg.meet_link, type: "weekly", callType: cfg.call_type || "platform",
        minutes: callMinutes(cfg), tz: cfg.tz, dow: cfg.day_of_week };
    }
  }
  return null;
}

export function getAdHocCalls() {
  // Legacy localStorage fallback — Supabase version used when available
  try { return JSON.parse(localStorage.getItem("bam_adhoc_calls") || "[]"); } catch { return []; }
}

export async function getAdHocCallsFromSupabase() {
  try {
    const { supabase } = await import("../../supabaseClient");
    const { data } = await supabase.from("adhoc_calls").select("*").order("created_at", { ascending: false });
    return (data || []).map(c => ({ id: c.id, title: c.title, dateTime: `${c.call_date}T${c.call_time}`, meetLink: c.meet_link }));
  } catch { return getAdHocCalls(); }
}

export function getAllUpcomingCalls(adHocOverride, config) {
  const cfg = config || FALLBACK_CONFIG;
  const now = Date.now();
  const weeklyCalls = getNextMondays(8, cfg).map((d, i) => ({
    id: `weekly-${i}`, title: cfg.title, dateTime: d.getTime(),
    meetLink: cfg.meet_link, type: "weekly",
  }));
  const adHoc = (adHocOverride || getAdHocCalls()).map(c => ({
    ...c, dateTime: new Date(c.dateTime).getTime(), type: "adhoc",
  }));
  return [...weeklyCalls, ...adHoc].filter(c => c.dateTime > now).sort((a, b) => a.dateTime - b.dateTime);
}

// Multi-config variant: builds the upcoming weekly instances for EACH config
// row (e.g. the Platform call + the Cert call), merges with ad-hoc calls, and
// returns them sorted soonest-first. Each call carries its own title/meetLink/
// callType so the widget can render + link them independently. Pass only the
// configs the viewer is allowed to see (cert call is filtered upstream).
export function getAllUpcomingCallsMulti(configs, adHocOverride) {
  const list = configs && configs.length ? configs : [FALLBACK_CONFIG];
  const now = Date.now();
  const weekly = list.flatMap((cfg, ci) =>
    getNextMondays(8, cfg).map((d, i) => ({
      id: `weekly-${cfg.call_type || ci}-${i}`,
      title: cfg.title,
      dateTime: d.getTime(),
      meetLink: cfg.meet_link,
      type: "weekly",
      callType: cfg.call_type || "platform",
      minutes: callMinutes(cfg),
      tz: cfg.tz,
      dow: cfg.day_of_week,
    }))
  );
  const adHoc = (adHocOverride || getAdHocCalls()).map(c => ({
    ...c, dateTime: new Date(c.dateTime).getTime(), type: "adhoc",
  }));
  return [...weekly, ...adHoc].filter(c => c.dateTime > now).sort((a, b) => a.dateTime - b.dateTime);
}

export function fmtCountdown(ms) {
  if (ms <= 0) return "Now";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}
